import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCaseStudyById, useCaseStudy } from "@/hooks/useCaseStudy";
import { CaseStudyPreviewContent } from "@/components/dashboard/profile/portfolio/case-study/CaseStudyPreviewContent";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Upload, ChevronRight } from "lucide-react";
import CustomLayout from "@/components/layout/CustomLayout";
import { useUserDropdown } from "@/hooks/useUserDropdown";

export default function CaseStudyPreviewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSaving, setIsSaving] = useState(false);
  const [savingAsDraft, setSavingAsDraft] = useState(false);
  const [isBreadcrumbVisible, setIsBreadcrumbVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const { data: caseStudy, isLoading, error } = useCaseStudyById(id || '');
  const { updateCaseStudy, publishCaseStudy } = useCaseStudy();
  const { displayName } = useUserDropdown();

  // Scroll behavior for breadcrumb
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        // Scrolling up or at the top - show breadcrumb
        setIsBreadcrumbVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px - hide breadcrumb
        setIsBreadcrumbVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleSaveAsDraft = async () => {
    if (!caseStudy) return;
    
    setIsSaving(true);
    setSavingAsDraft(true);
    
    try {
      await updateCaseStudy.mutateAsync({
        id: caseStudy.id,
        status: 'draft',
        is_featured: false,
      });
    } catch (error) {
      console.error('Error saving as draft:', error);
    } finally {
      setIsSaving(false);
      setSavingAsDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!caseStudy) return;
    
    setIsSaving(true);
    setSavingAsDraft(false);
    
    try {
      if (caseStudy.status === 'published') {
        await updateCaseStudy.mutateAsync({
          id: caseStudy.id,
          status: 'published'
        });
      } else {
        await publishCaseStudy.mutateAsync(caseStudy.id);
      }
    } catch (error) {
      console.error('Error publishing:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (error || !caseStudy) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Error Loading Case Study</h2>
          <p className="text-gray-600 mb-4">
            {error?.message || "There was an error loading the case study."}
          </p>
          <Button
            onClick={() => navigate("/dashboard/portfolio")}
            className="bg-gray-900 text-white hover:bg-gray-800"
          >
            Back to Portfolio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-transparent animate-fade-in flex flex-col">
      {/* Black Top Header Bar - Fixed at top */}
      <div className="fixed top-0 left-0 right-0 bg-black text-white px-6 py-3 z-50">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/dashboard/portfolio/case-study/${caseStudy?.id}`)}
              className="flex items-center gap-2 text-xs text-white hover:bg-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
           <div className="text-[#F99539] px-3 py-1 rounded text-xs font-medium flex items-center gap-2">
              <img src="/eye-icon.svg" alt="Eye Icon" className="h-4 w-4" />
              Preview Mode Active
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSaveAsDraft}
              disabled={isSaving}
              className="bg-black text-xs border-none text-white"
            >
              <img src="/save-icon.svg" alt="Save Icon" className="h-4 w-4" />
              {isSaving && savingAsDraft ? "Saving..." : "Save Draft"}
            </Button>
            <Button 
              size="sm" 
              onClick={handlePublish}
              disabled={isSaving}
              className="bg-white text-xs text-black hover:bg-orange-500 hover:text-white"
            >
              <img src="/publish-icon.svg" alt="Upload Icon" className="h-4 w-4" />
              {isSaving && !savingAsDraft
                ? (caseStudy?.status === 'published' ? 'Updating...' : 'Publishing...') 
                : (caseStudy?.status === 'published' ? 'Update' : 'Publish')
              }
            </Button>
          </div>
        </div>
      </div>

      {/* Breadcrumb Navigation - Fixed below headers */}
      <div className={`fixed top-[129px] left-0 right-0 w-full bg-gray-200 bg-opacity-30 transition-all duration-300 ease-out lg:backdrop-blur-md items-center py-4 z-30 ${
        isBreadcrumbVisible 
          ? 'opacity-100 translate-y-0 shadow-[inset_0px_4px_7px_-5px_rgba(0,0,0,0.3)]' 
          : 'opacity-0 -translate-y-full shadow-none'
      }`}>
        <nav className="max-w-4xl mx-auto flex items-center space-x-2 text-gray-600 py-2 ">
          <span className="text-black font-medium text-xs">
            {displayName || 'User'}
          </span>
          <span className="text-black text-xs">
            <ChevronRight className="h-3 w-3" />
          </span>
          <span className="text-black text-xs">Case Study</span>
          <span className="text-black text-xs">
            <ChevronRight className="h-3 w-3" />
          </span>
          <span className="text-black font-medium text-xs">
            {caseStudy?.title || 'Untitled Case Study'}
          </span>
        </nav>
      </div>

      {/* CustomLayout with header and content */}
      <CustomLayout>
        <div className="bg-white flex flex-col items-stretch pt-[200px]">
          <div className="max-w-4xl mx-auto py-8">
            <CaseStudyPreviewContent caseStudy={caseStudy} />
          </div>
        </div>
      </CustomLayout>
    </div>
  );
}
